import { useState, useMemo } from 'react';
import {
  useStaff,
  useCreateStaff,
  useUpdateStaff,
  useDeleteStaff,
  useAssignStaffCenter,
  useRemoveStaffCenter,
  useServices,
} from '@hooks/useAdminResources';
import { useAuth } from '@hooks/useAuth';
import { useActiveCenter, ALL_CENTERS } from '@hooks/useActiveCenter';
import { useConfirm } from '@hooks/useConfirm';
import { adminStaffApi } from '@api/client';
import type { Staff } from '@api/types';
import * as Icons from '@components/Icons';
import { PageHead } from '@components/admin/PageHead';
import { RowMenu } from '@components/admin/RowMenu';
import { StaffModal, type CenterOption } from './staff/StaffModal';

function initials(name: string) {
  return name.split(' ').map((p) => p.charAt(0)).join('').slice(0, 2).toUpperCase();
}

function tone(name: string) {
  let h = 0;
  for (let i = 0; i < name.length; i++) h = (h * 31 + name.charCodeAt(i)) | 0;
  return Math.abs(h) % 5;
}

const ROLE_LABEL: Record<string, string> = {
  center_admin: 'Administrador',
  staff: 'Staff',
};

export function StaffPage() {
  const { user } = useAuth();
  const { activeCenterId, setActiveCenterId, availableCenters, canSwitch, supportsAll } = useActiveCenter();
  const confirm = useConfirm();
  // `'all'` becomes empty so the staff list query uses the backend's
  // role-default scope.
  const centerId = activeCenterId === ALL_CENTERS ? '' : activeCenterId;
  const isSuper = user?.role === 'superadmin';

  const { data: staffList = [], isLoading } = useStaff(centerId);
  // Catálogo global de servicios (post-0028) — solo lo lee superadmin para
  // configurar qué servicios ofrece cada staff. center_admin no lo necesita.
  const { data: servicesList = [] } = useServices();
  const createMutation = useCreateStaff(centerId);
  const updateMutation = useUpdateStaff(centerId);
  const deleteMutation = useDeleteStaff(centerId);
  const assignCenter = useAssignStaffCenter(centerId);
  const removeCenter = useRemoveStaffCenter(centerId);

  const centerOptions = useMemo<CenterOption[]>(
    () => availableCenters.map((c) => ({ id: c.id, name: c.name })),
    [availableCenters],
  );
  const centerNameById = useMemo(
    () => new Map(centerOptions.map((c) => [c.id, c.name])),
    [centerOptions],
  );

  const [q, setQ] = useState('');
  const [modalStaff, setModalStaff] = useState<Staff | null | undefined>(undefined);

  const filtered = useMemo(() => {
    const s = q.toLowerCase();
    return staffList.filter((st) => {
      if (activeCenterId !== ALL_CENTERS && !st.centerIds.includes(activeCenterId)) return false;
      if (s && !(st.name.toLowerCase().includes(s) || (st.email?.toLowerCase().includes(s) ?? false))) return false;
      return true;
    });
  }, [staffList, q, activeCenterId]);

  const activeCount = staffList.filter((s) => s.active).length;

  async function handleSave(data: Record<string, unknown>) {
    if (modalStaff === null) {
      await createMutation.mutateAsync(data);
    } else if (modalStaff) {
      await updateMutation.mutateAsync({ id: modalStaff.id, data });
    }
    setModalStaff(undefined);
  }

  async function handleToggleGoogleSync(st: Staff, disable: boolean) {
    const ok = await confirm({
      title: disable ? 'Desactivar sync Google' : 'Reactivar sync Google',
      message: disable
        ? `Las nuevas reservas de "${st.name}" dejarán de sincronizarse con su Google Calendar. Las que ya estén creadas en su calendario no se tocan. Usa esto en despidos.`
        : `Las reservas de "${st.name}" volverán a sincronizarse con su Google Calendar conectado.`,
      tone: disable ? 'danger' : undefined,
      confirmLabel: disable ? 'Desactivar' : 'Reactivar',
    });
    if (!ok) return;
    try {
      await adminStaffApi.setGoogleSyncDisabled(st.id, disable);
      // Refetch the list so the row menu reflects the new state.
      window.location.reload();
    } catch {
      // confirm dialog already swallows; ignore further
    }
  }

  async function handleDelete(st: Staff) {
    const ok = await confirm({
      title: 'Desactivar profesional',
      message: `¿Desactivar al profesional "${st.name}"? Perderá acceso al panel.`,
      tone: 'danger',
      confirmLabel: 'Desactivar',
    });
    if (ok) {
      await deleteMutation.mutateAsync(st.id);
    }
  }

  return (
    <div>
      <PageHead
        title="Staff"
        sub={`${String(staffList.length)} profesionales · ${String(activeCount)} activos`}
        actions={
          <button className="btn" data-variant="primary" onClick={() => { setModalStaff(null); }}>
            <Icons.Plus size={14} /> Invitar profesional
          </button>
        }
      />

      <div className="toolbar">
        <div className="search">
          <Icons.Search size={14} className="ico" />
          <input
            className="input"
            placeholder="Buscar profesional…"
            value={q}
            onChange={(e) => { setQ(e.target.value); }}
          />
        </div>
        {canSwitch && (
          <select
            className="select"
            style={{ width: 'auto' }}
            value={activeCenterId}
            onChange={(e) => { setActiveCenterId(e.target.value); }}
          >
            {supportsAll && <option value={ALL_CENTERS}>Todos los centros</option>}
            {availableCenters.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
        )}
        <div className="toolbar-spacer" />
        <span className="mono" style={{ fontSize: 11.5, color: 'var(--fg-soft)' }}>
          {filtered.length} resultados
        </span>
      </div>

      <div className="card">
        <table className="table">
          <thead>
            <tr>
              <th>Profesional</th>
              <th>Rol</th>
              {isSuper && <th>Centros</th>}
              <th>Estado</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {isLoading && (
              <tr><td colSpan={isSuper ? 5 : 4} style={{ textAlign: 'center', padding: 32, color: 'var(--fg-muted)' }}>Cargando…</td></tr>
            )}
            {!isLoading && filtered.length === 0 && (
              <tr><td colSpan={isSuper ? 5 : 4} style={{ textAlign: 'center', padding: 32, color: 'var(--fg-muted)' }}>Sin profesionales</td></tr>
            )}
            {filtered.map((st) => (
              <tr key={st.id} data-clickable="true" onClick={() => { setModalStaff(st); }}>
                <td>
                  <div className="user-cell">
                    <div className="avatar" data-tone={tone(st.name)}>{initials(st.name)}</div>
                    <div className="user-cell-info">
                      <span className="user-cell-name">{st.name}</span>
                      <span className="user-cell-meta mono">{st.email ?? '—'}</span>
                    </div>
                  </div>
                </td>
                <td>
                  <span style={{ fontSize: 13 }}>{ROLE_LABEL[st.role] ?? st.role}</span>
                </td>
                {isSuper && (
                  <td>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                      {st.centerIds.length === 0
                        ? <span style={{ fontSize: 12, color: 'var(--fg-soft)' }}>—</span>
                        : st.centerIds.map((cid) => (
                            <span key={cid} className="pill" data-tone="neutral">{centerNameById.get(cid) ?? cid}</span>
                          ))}
                    </div>
                  </td>
                )}
                <td>
                  {st.active
                    ? <span className="pill" data-tone="ok">Activo</span>
                    : <span className="pill" data-tone="neutral">Inactivo</span>}
                </td>
                <td className="cell-narrow">
                  <div className="row-actions">
                    <button
                      className="icon-btn"
                      data-variant="ghost"
                      onClick={(e) => { e.stopPropagation(); setModalStaff(st); }}
                    >
                      <Icons.Edit size={13} />
                    </button>
                    <RowMenu items={[
                      { icon: Icons.Edit, label: 'Editar', onClick: () => { setModalStaff(st); } },
                      ...(isSuper ? [
                        null,
                        st.googleSyncDisabled
                          ? { icon: Icons.Globe, label: 'Reactivar sync Google', onClick: () => { void handleToggleGoogleSync(st, false); } }
                          : { icon: Icons.Globe, label: 'Desactivar sync Google (despido)', tone: 'danger', onClick: () => { void handleToggleGoogleSync(st, true); } },
                      ] : []),
                      null,
                      { icon: Icons.Trash, label: 'Desactivar usuario', tone: 'danger', onClick: () => { void handleDelete(st); } },
                    ]} />
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {modalStaff !== undefined && (
        <StaffModal
          key={modalStaff?.id ?? 'new'}
          staff={modalStaff}
          onClose={() => { setModalStaff(undefined); }}
          onSave={handleSave}
          isSaving={createMutation.isPending || updateMutation.isPending}
          isSuper={isSuper}
          centers={centerOptions}
          onAddCenter={modalStaff ? (cid) => assignCenter.mutateAsync({ staffId: modalStaff.id, targetCenterId: cid }) : undefined}
          onRemoveCenter={modalStaff ? (cid) => removeCenter.mutateAsync({ staffId: modalStaff.id, targetCenterId: cid }) : undefined}
          allServices={servicesList}
        />
      )}

    </div>
  );
}
